import React, { useRef, useState } from "react";
import styled from "styled-components";
import { FiImage, FiUpload } from "react-icons/fi";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Label, ErrorMessage } from "../ui/Input";
import { theme } from "../../styles/theme";

const Layout = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const DropZone = styled.div`
  border: 2px dashed
    ${({ active }) => (active ? theme.colors.accent.primary : theme.colors.stroke.strong)};
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
`;

const SliderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["2xs"]};
`;

const FileList = styled.div`
  max-height: 120px;
  overflow: auto;
  border: ${theme.borders.base};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.background.canvas};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

function filterImages(files) {
  return (files || []).filter((file) => file?.type?.startsWith("image/"));
}

function GenerateActorsDialog({ isOpen, onClose, onSubmit, loading }) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [count, setCount] = useState(4);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) {
    return null;
  }

  const appendFiles = (incomingFiles) => {
    const images = filterImages(Array.from(incomingFiles || []));
    if (!images.length) return;
    setFiles((prev) => [...prev, ...images]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!files.length) {
      setError("Add at least one reference image.");
      return;
    }
    onSubmit?.({ files, count });
    setFiles([]);
    setCount(4);
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={loading ? undefined : onClose} title="Generate actors">
      <Layout onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"] }}>
          <Label>Reference images</Label>
          <DropZone
            active={dragActive}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              appendFiles(event.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.xs }}>
              <span style={{ color: theme.colors.text.primary }}>
                <FiUpload /> Drop images here
              </span>
              <span>or click to pick files</span>
            </div>
          </DropZone>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(event) => appendFiles(event.target.files)}
            disabled={loading}
          />
          {files.length > 0 && (
            <FileList>
              {files.map((file, index) => (
                <div key={`${file.name}_${index}`}>
                  <FiImage /> {file.name}
                </div>
              ))}
            </FileList>
          )}
        </div>

        <SliderRow>
          <Label htmlFor="actor-count">Actor generation count: {count}</Label>
          <input
            id="actor-count"
            type="range"
            min={1}
            max={20}
            value={count}
            disabled={loading}
            onChange={(event) => setCount(Number(event.target.value))}
          />
        </SliderRow>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm }}>
          <Button type="button" variant="ghost" disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Generating..." : "Generate actors"}
          </Button>
        </div>
      </Layout>
    </Modal>
  );
}

export default GenerateActorsDialog;
